// =============================================================================
// GOUV CONFIG - Widget Gouvernance : Configuration Globale
// Créé le 22/02/2026
// Gestion des paramètres système (tenants, modules, variables)
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Settings,
  Building2,
  Layers,
  Key,
  Globe,
  Shield,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Copy,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Users,
  Database,
  Server,
  Info
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import { Input } from "../../../components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Catalogue des hubs : chargé depuis /api/platform/hubs-catalog (E4 —
// migration depuis hardcode vers source canonique HubDefinition.HUBS).
// Le mapping icon string → composant Lucide reste côté frontend (les
// icônes sont des composants React, non sérialisables côté backend).
const HUB_ICON_MAP = {
  Users, Database, Layers, Server, Building2, Shield,
  // Aliases pour anciens hubs si l'icône envoyée n'est pas dans la liste —
  // fallback Layers utilisé si key absent.
};

export default function GouvConfig({ onRefreshStats, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tenants");
  const [tenants, setTenants] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);  // E4 : DB-driven
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [systemConfig, setSystemConfig] = useState({
    api_url: API,
    environment: 'production',
    version: '2.0.0'
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Charger les tenants + le catalogue de hubs en parallèle
      const [tenantsRes, hubsRes] = await Promise.all([
        fetch(`${API}/api/platform/tenants`, { headers }),
        fetch(`${API}/api/platform/hubs-catalog`, { headers }),
      ]);
      if (tenantsRes.ok) {
        const data = await tenantsRes.json();
        setTenants(Array.isArray(data) ? data : data.tenants || []);
      }
      if (hubsRes.ok) {
        const data = await hubsRes.json();
        // Résoudre l'icône string → composant Lucide. Fallback sur Layers
        // si l'icône envoyée par le backend n'est pas mappée côté front.
        const mapped = (data.hubs || []).map(h => ({
          ...h,
          icon: HUB_ICON_MAP[h.icon] || Layers,
        }));
        setAvailableModules(mapped);
      }
    } catch (err) {
      console.error("Erreur chargement config:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleModule = async (tenantId, moduleId, enabled) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/admin/tenants/${tenantId}/modules`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ module: moduleId, enabled })
      });

      if (res.ok) {
        toast.success(`Module ${moduleId} ${enabled ? 'activé' : 'désactivé'}`);
        loadData();
      } else {
        toast.error("Erreur lors de la modification");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Settings className="w-7 h-7 text-zinc-400" />
            Configuration Globale
          </h2>
          <p className="text-zinc-500 mt-1">Paramètres système et gestion des tenants</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          disabled={loading}
          className="border-zinc-700"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1">
          <TabsTrigger value="tenants" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Tenants
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1">
              {tenants.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Modules
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Server className="w-4 h-4" />
            Système
          </TabsTrigger>
        </TabsList>

        {/* Tab Tenants */}
        <TabsContent value="tenants" className="mt-4">
          <div className="space-y-3">
            {tenants.length > 0 ? (
              tenants.map(tenant => (
                <div
                  key={tenant.id || tenant._id || tenant.slug}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden"
                  data-testid={`tenant-${tenant.slug}`}
                >
                  {/* Tenant Header */}
                  <div
                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
                    onClick={() => setExpandedTenant(expandedTenant === tenant.slug ? null : tenant.slug)}
                  >
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{tenant.nom || tenant.name || tenant.slug}</span>
                        {tenant.actif !== false && (
                          <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                            Actif
                          </Badge>
                        )}
                        {tenant.trial && (
                          <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                            Trial
                          </Badge>
                        )}
                        {tenant.is_platform_owner && (
                          <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                            Platform Owner
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-zinc-500">{tenant.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {tenant.hubs_actifs && (
                        <Badge variant="outline" className="text-xs border-zinc-700">
                          {tenant.hubs_actifs.length} hubs
                        </Badge>
                      )}
                      {expandedTenant === tenant.slug ? (
                        <ChevronUp className="w-5 h-5 text-zinc-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-zinc-500" />
                      )}
                    </div>
                  </div>

                  {/* Tenant Details */}
                  {expandedTenant === tenant.slug && (
                    <div className="px-4 pb-4 pt-0 border-t border-zinc-800 bg-zinc-950/50">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="text-xs text-zinc-500">ID</label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-sm text-zinc-300 bg-zinc-800 px-2 py-1 rounded truncate max-w-[200px]">
                              {tenant.id || tenant._id}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyToClipboard(tenant.id || tenant._id)}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-zinc-500">Slug</label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-sm text-zinc-300 bg-zinc-800 px-2 py-1 rounded">
                              {tenant.slug}
                            </code>
                          </div>
                        </div>
                        {tenant.description && (
                          <div className="col-span-2">
                            <label className="text-xs text-zinc-500">Description</label>
                            <p className="text-sm text-zinc-300 mt-1">{tenant.description}</p>
                          </div>
                        )}
                        {tenant.identite?.siret && (
                          <div>
                            <label className="text-xs text-zinc-500">SIRET</label>
                            <p className="text-sm text-zinc-300 mt-1">{tenant.identite.siret}</p>
                          </div>
                        )}
                        {tenant.created_at && (
                          <div>
                            <label className="text-xs text-zinc-500">Créé le</label>
                            <p className="text-sm text-zinc-300 mt-1">
                              {new Date(tenant.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Hubs activés */}
                      {tenant.hubs_actifs && tenant.hubs_actifs.length > 0 && (
                        <div className="mt-4">
                          <label className="text-xs text-zinc-500 mb-2 block">Hubs activés</label>
                          <div className="flex flex-wrap gap-2">
                            {availableModules.map(mod => {
                              const isEnabled = tenant.hubs_actifs.includes(mod.id);
                              return (
                                <div
                                  key={mod.id}
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                                    isEnabled
                                      ? 'bg-emerald-500/10 border-emerald-500/30'
                                      : 'bg-zinc-800/50 border-zinc-700'
                                  }`}
                                >
                                  {isEnabled ? (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                  ) : (
                                    <XCircle className="w-3.5 h-3.5 text-zinc-500" />
                                  )}
                                  <span className={`text-sm ${isEnabled ? 'text-emerald-400' : 'text-zinc-500'}`}>
                                    {mod.name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Aucun tenant configuré</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab Modules */}
        <TabsContent value="modules" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableModules.map(mod => {
              const Icon = mod.icon;
              // Compter combien de tenants ont ce hub actif
              const activeCount = tenants.filter(t =>
                t.hubs_actifs && t.hubs_actifs.includes(mod.id)
              ).length;

              return (
                <div
                  key={mod.id}
                  className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{mod.name}</span>
                        <Badge variant="outline" className="text-xs border-zinc-700">
                          {mod.id}
                        </Badge>
                      </div>
                      <p className="text-sm text-zinc-500">{mod.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-white">{activeCount}</div>
                      <p className="text-xs text-zinc-500">tenant(s)</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-zinc-900/30 border border-zinc-800 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm text-zinc-300">Gestion des hubs</p>
                <p className="text-xs text-zinc-500 mt-1">
                  L'activation/désactivation des hubs par tenant se fait depuis la page de détail
                  du tenant. Les hubs contrôlent l'accès aux grandes fonctionnalités de la plateforme.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Tab Système */}
        <TabsContent value="system" className="mt-4">
          <div className="space-y-4">
            {/* Informations système */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Server className="w-5 h-5 text-cyan-400" />
                Informations système
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500">API URL</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm text-zinc-300 bg-zinc-800 px-2 py-1 rounded flex-1 truncate">
                      {systemConfig.api_url}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(systemConfig.api_url)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Environnement</label>
                  <div className="mt-1">
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      {systemConfig.environment}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Version</label>
                  <p className="text-sm text-zinc-300 mt-1">{systemConfig.version}</p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Policy Engine</label>
                  <div className="mt-1">
                    <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                      Transversal v2.0
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Variables d'environnement (simulées) */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                Variables critiques
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-zinc-300">MONGO_URL</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                    Configuré
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-zinc-300">DB_NAME</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                    Configuré
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-zinc-300">OPENAI_API_KEY</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                    Configuré
                  </Badge>
                </div>
              </div>
            </div>

            {/* Actions système */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Actions système
              </h3>
              <div className="flex flex-wrap gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                        onClick={async () => {
                          const res = await fetch(`${API}/api/policy/cache/invalidate`, {
                            method: 'POST',
                            headers: localStorage.getItem('session_token')
                              ? { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
                              : {}
                          });
                          if (res.ok) {
                            toast.success("Cache Policy invalidé");
                          }
                        }}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Invalider cache Policy
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Force le rechargement des règles depuis la DB</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-zinc-700"
                        onClick={async () => {
                          const res = await fetch(`${API}/api/policy/initialize`, {
                            method: 'POST',
                            headers: localStorage.getItem('session_token')
                              ? { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
                              : {}
                          });
                          if (res.ok) {
                            const data = await res.json();
                            toast.success(`Règles: ${data.status} (${data.count} règles)`);
                          }
                        }}
                      >
                        <Database className="w-4 h-4 mr-2" />
                        Réinitialiser règles
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Synchronise les règles GROOT avec la DB</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
